import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, CheckCircle, ChevronDown, Clock, Key, Link2, Loader2, Plus, Receipt, RotateCcw, Search, Trash2, XCircle } from 'lucide-react';
import { fmtDate, fmtTime } from './admin/format';
import { EYEBROW, INK, IconTile, LYRA_INPUT, LYRA_INSET, LYRA_OUTLINE_BUTTON, MUTED, Pill } from './admin/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

// Step-by-step automations (backend: automation_flows.py) — Zapier-style: a trigger, then steps run top to bottom.
// Delays and GHL lookups' retries are real waits (they survive restarts); every sale gets one run, shown in Runs.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
const DEFAULT_ITEM = 'Diabetes Reversal Strategy Session';

export const STEP_TYPES = {
  delay: { icon: Clock, label: 'Delay', desc: 'Wait before the next step', tone: 'gold' },
  ghl_find_contact: { icon: Search, label: 'Find GHL contact', desc: 'Look the person up in GoHighLevel by email', tone: 'teal' },
  webhook: { icon: Link2, label: 'Send webhook', desc: 'POST to n8n, Zapier, GHL or any URL', tone: 'blue' },
  ghl_order: { icon: Receipt, label: 'GHL invoice (paid)', desc: 'Create a paid GoHighLevel invoice for the sale', tone: 'red', purchaseOnly: true },
};
const SENDS = ['webhook', 'ghl_order'];

const STEP_DEFAULTS = {
  delay: { minutes: 5 },
  ghl_find_contact: { fields: 'TetherFbClid, tetherIP', retries: [30, 5], onNotFound: 'continue' },
  webhook: { url: '', method: 'POST', includeData: true, headers: [], format: 'event', productId: '', priceId: 'auto', itemName: DEFAULT_ITEM },
  ghl_order: { productId: '', priceId: 'auto', itemName: DEFAULT_ITEM, businessName: 'Dr. Jason Shumard' },
};
export const blankStep = (type) => ({ id: newId(), type, name: '', ...STEP_DEFAULTS[type] });

const headersToArray = (h) => Object.entries(h || {}).map(([key, value]) => ({ key, value: value || '' }));
const headersToObject = (rows) => {
  const o = {};
  (rows || []).forEach((r) => { if (r.key && r.key.trim()) o[r.key.trim()] = r.value || ''; });
  return Object.keys(o).length ? o : null;
};

// An automation's steps for the editor: its steps, or (made before steps existed) its actions as send steps.
export const stepsFromAutomation = (a) => {
  const raw = a.steps?.length ? a.steps : (a.actions || (a.action ? [a.action] : [])).map((x) => ({ ...x, type: x.type || 'webhook' }));
  return raw.map((s) => ({
    ...blankStep(s.type), id: s.id || newId(), type: s.type, name: s.name || '',
    ...(s.type === 'delay' && { minutes: s.minutes || 5 }),
    ...(s.type === 'ghl_find_contact' && { fields: (s.fields || []).join(', '), retries: s.retries || [], onNotFound: s.on_not_found || 'continue' }),
    ...(s.type === 'webhook' && { url: s.url || '', method: s.method || 'POST', includeData: s.include_data !== false,
      headers: headersToArray(s.headers), format: s.format || 'event', productId: s.product_id || '', priceId: s.price_id || 'auto',
      itemName: s.item_name || DEFAULT_ITEM }),
    ...(s.type === 'ghl_order' && { productId: s.product_id || '', priceId: s.price_id || 'auto', itemName: s.item_name || DEFAULT_ITEM,
      businessName: s.business_name || '' }),
  }));
};

export const stepToApi = (s) => {
  const base = { id: s.id, type: s.type, name: s.name || null };
  if (s.type === 'delay') return { ...base, minutes: Number(s.minutes) };
  if (s.type === 'ghl_find_contact') return { ...base, fields: s.fields.split(',').map((f) => f.trim()).filter(Boolean),
    retries: s.retries.map(Number).filter((m) => m > 0), on_not_found: s.onNotFound };
  if (s.type === 'webhook') return { ...base, url: s.url.trim(), method: s.method, include_data: s.includeData, headers: headersToObject(s.headers),
    format: s.format, ...(s.format === 'ghl_order' && { product_id: s.productId, price_id: s.priceId, item_name: s.itemName || null }) };
  return { ...base, product_id: s.productId, price_id: s.priceId, item_name: s.itemName || null, business_name: s.businessName || null };
};

// What's missing before this step can be saved ('' = ready).
export const stepProblem = (s, trigger) => {
  const T = STEP_TYPES[s.type];
  if ((T.purchaseOnly || (s.type === 'webhook' && s.format === 'ghl_order')) && trigger !== 'checkout_purchase') return `${T.label}: only works on the Checkout purchase trigger`;
  if (s.type === 'delay' && !(Number(s.minutes) > 0 && Number(s.minutes) <= 10080)) return 'Delay: enter the minutes to wait (up to 7 days)';
  if (s.type === 'ghl_find_contact' && s.retries.some((m) => !(Number(m) > 0 && Number(m) <= 1440))) return 'Find GHL contact: each retry wait must be 1–1440 minutes';
  if (s.type === 'webhook' && !/^https?:\/\/\S+$/.test(s.url.trim())) return 'Send webhook: enter the URL';
  if (s.type === 'webhook' && s.format === 'ghl_order' && !s.productId) return 'Send webhook: choose the GHL product for the order';
  if (s.type === 'ghl_order' && !(s.productId && s.priceId)) return 'GHL invoice: choose the product and price';
  return '';
};
export const stepsProblem = (steps, trigger) => (!steps.some((s) => SENDS.includes(s.type))
  ? 'Add at least one step that sends something (a webhook)' : steps.map((s) => stepProblem(s, trigger)).find(Boolean) || '');

const host = (url) => { try { return new URL(url).host; } catch { return url || 'webhook'; } };
const mins = (m) => (Number(m) >= 60 && Number(m) % 60 === 0 ? `${Number(m) / 60} h` : `${m} min`);

export const stepSummary = (s) => {
  if (s.type === 'delay') return `Wait ${mins(s.minutes)}`;
  if (s.type === 'ghl_find_contact') {
    const f = s.fields ? ` → contact id, ${s.fields}` : ' → contact id';
    const r = s.retries.length ? ` · not found: retry (email + phone) after ${s.retries.map(mins).join(', then ')}, then ${s.onNotFound === 'stop' ? 'stop' : 'carry on without it'}` : '';
    return `Search by email${f}${r}`;
  }
  if (s.type === 'webhook') return `${s.method} ${host(s.url)}${s.format === 'ghl_order' ? ' · GHL order format' : ''}`;
  return 'Contact → invoice → recorded payment';
};

// GHL product + price pickers (lists come from GHL).
function GhlProductFields({ step, onChange, invoice }) {
  const [products, setProducts] = useState(null);
  const [prices, setPrices] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get(`${API}/admin/ghl/products`, auth()).then((r) => setProducts(r.data.products))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load GoHighLevel products'));
  }, []);
  useEffect(() => {
    if (!step.productId) { setPrices(null); return; }
    setPrices(null);
    axios.get(`${API}/admin/ghl/products/${step.productId}/prices`, auth()).then((r) => setPrices(r.data.prices))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load prices'));
  }, [step.productId]);
  const money = (p) => (p.amount != null ? `${p.amount} ${(p.currency || '').toUpperCase()}` : '');
  return (
    <div className="space-y-3">
      {error && <p className="text-xs text-[#b42318]">{error}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className={`text-xs ${INK}`}>GHL product</Label>
          <Select value={step.productId || undefined} onValueChange={(v) => onChange({ productId: v, priceId: 'auto' })} disabled={!products}>
            <SelectTrigger className={LYRA_INPUT}><SelectValue placeholder={products ? 'Choose a product' : 'Loading…'} /></SelectTrigger>
            <SelectContent>{(products || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className={`text-xs ${INK}`}>Price</Label>
          <Select value={step.priceId || undefined} onValueChange={(v) => onChange({ priceId: v })} disabled={!step.productId || !prices}>
            <SelectTrigger className={LYRA_INPUT}><SelectValue placeholder={!step.productId ? 'Choose a product first' : prices ? 'Choose a price' : 'Loading…'} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Match the amount paid{prices?.length ? ` (${prices.map((p) => p.amount).join(' / ')})` : ''}</SelectItem>
              {(prices || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name || 'Price'} {money(p) && `· ${money(p)}`}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className={`text-xs ${INK}`}>Item name</Label>
          <Input value={step.itemName} onChange={(e) => onChange({ itemName: e.target.value })} className={LYRA_INPUT} />
        </div>
        {invoice && (
          <div className="space-y-1.5">
            <Label className={`text-xs ${INK}`}>Business name on the invoice</Label>
            <Input value={step.businessName} onChange={(e) => onChange({ businessName: e.target.value })} className={LYRA_INPUT} />
          </div>
        )}
      </div>
    </div>
  );
}

function StepFields({ step, onChange }) {
  if (step.type === 'delay') {
    return (
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label className={`text-xs ${INK}`}>Wait</Label>
          <div className="flex items-center gap-2">
            <Input type="number" min={1} max={10080} value={step.minutes} onChange={(e) => onChange({ minutes: e.target.value })} className={`w-28 ${LYRA_INPUT}`} />
            <span className={`text-sm ${MUTED}`}>minutes</span>
          </div>
        </div>
        <p className={`pb-2 text-xs ${MUTED}`}>A real wait: it survives restarts. A manual send from Purchases skips it.</p>
      </div>
    );
  }
  if (step.type === 'ghl_find_contact') {
    const setRetry = (i, v) => onChange({ retries: step.retries.map((m, j) => (j === i ? v : m)) });
    return (
      <div className="space-y-3">
        <p className={`text-xs ${MUTED}`}>Searches GoHighLevel for the buyer's email (and on retries, if the email isn't found, their phone — for contacts whose email was changed or merged). Gives the next steps their GHL <span className="font-medium">contact id</span> and the custom fields below (a GHL-order webhook uses <span className="font-medium">TetherFbClid</span> and <span className="font-medium">tetherIP</span>).</p>
        <div className="space-y-1.5">
          <Label className={`text-xs ${INK}`}>Custom fields to read (comma-separated, as named in GHL)</Label>
          <Input value={step.fields} onChange={(e) => onChange({ fields: e.target.value })} placeholder="TetherFbClid, tetherIP" className={LYRA_INPUT} />
        </div>
        <div className="space-y-2">
          <Label className={`text-xs ${INK}`}>If they're not in GHL yet</Label>
          {step.retries.map((m, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className={`w-28 ${MUTED}`}>Retry {i + 1}: wait</span>
              <Input type="number" min={1} max={1440} value={m} onChange={(e) => setRetry(i, e.target.value)} className={`h-8 w-24 ${LYRA_INPUT}`} />
              <span className={MUTED}>min, then search again</span>
              <Button type="button" variant="ghost" size="icon" className="size-7 !rounded-none text-[#8a94a6] hover:bg-[#fdecec] hover:text-[#b42318]"
                onClick={() => onChange({ retries: step.retries.filter((_, j) => j !== i) })} aria-label={`Remove retry ${i + 1}`}><Trash2 className="size-3.5" /></Button>
            </div>
          ))}
          {step.retries.length < 10 && (
            <Button type="button" variant="ghost" size="sm" className="!rounded-none text-[#2654cc] hover:bg-[#e8efff]" onClick={() => onChange({ retries: [...step.retries, 5] })}><Plus className="size-3" /> Add a retry</Button>
          )}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={MUTED}>Still not found after {step.retries.length ? 'the retries' : 'the first search'}:</span>
            <Select value={step.onNotFound} onValueChange={(v) => onChange({ onNotFound: v })}>
              <SelectTrigger className={`h-8 w-64 ${LYRA_INPUT}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="continue">Carry on without the contact</SelectItem>
                <SelectItem value="stop">Stop — send nothing</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }
  if (step.type === 'ghl_order') {
    return (
      <div className="space-y-3">
        <p className={`text-xs ${MUTED}`}>Upserts the buyer as a GHL contact, creates an invoice for this product and records the payment with their real card from Stripe (never sent to the customer; never added twice; sales that came from GHL are skipped).</p>
        <GhlProductFields step={step} onChange={onChange} invoice />
      </div>
    );
  }
  const headers = step.headers || [];
  const setHeader = (i, patch) => onChange({ headers: headers.map((h, j) => (j === i ? { ...h, ...patch } : h)) });
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className={`text-xs ${INK}`}>Webhook URL</Label>
        <Input value={step.url} onChange={(e) => onChange({ url: e.target.value })} placeholder="https://your-service.com/webhook" className={LYRA_INPUT} />
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label className={`text-xs ${INK}`}>Method</Label>
          <Select value={step.method} onValueChange={(v) => onChange({ method: v })}>
            <SelectTrigger className={`w-28 ${LYRA_INPUT}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="POST">POST</SelectItem><SelectItem value="GET">GET</SelectItem></SelectContent>
          </Select>
        </div>
        {step.method === 'POST' && (
          <div className="space-y-1.5">
            <Label className={`text-xs ${INK}`}>Body</Label>
            <Select value={step.includeData ? step.format : 'none'} onValueChange={(v) => onChange(v === 'none' ? { includeData: false, format: 'event' } : { includeData: true, format: v })}>
              <SelectTrigger className={`w-72 ${LYRA_INPUT}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="event">The event data (+ GHL contact, if found)</SelectItem>
                <SelectItem value="ghl_order">GHL native order format</SelectItem>
                <SelectItem value="none">No body</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {step.format === 'ghl_order' && step.includeData && (
        <div className={`${LYRA_INSET} space-y-3 bg-white p-3`}>
          <p className={`text-xs ${MUTED}`}>Sends the sale shaped like a native GoHighLevel order webhook: the contact (contact_id, name, email, phone, timezone, <span className="font-medium">tetherIP</span>, <span className="font-medium">TetherFbClid</span>, location) and the order (product / price ids, line item, currency, totals, order_id = the Stripe payment id). Put a <span className="font-medium">Find GHL contact</span> step before it for the contact id and TetherFbClid.</p>
          <GhlProductFields step={step} onChange={onChange} />
        </div>
      )}
      <div className="border-t border-[#e6e9ef] pt-3">
        <div className="mb-2 flex items-center justify-between">
          <Label className={`flex items-center gap-1 text-xs ${INK}`}><Key className="size-3 text-[#3565e9]" /> Headers (optional)</Label>
          <Button type="button" variant="ghost" size="sm" className="!rounded-none text-[#2654cc] hover:bg-[#e8efff]" onClick={() => onChange({ headers: [...headers, { key: '', value: '' }] })}><Plus className="size-3" /> Add header</Button>
        </div>
        <div className="space-y-2">
          {headers.map((h, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={h.key} onChange={(e) => setHeader(i, { key: e.target.value })} placeholder="Header name (e.g. X-API-Key)" className={`h-8 flex-1 text-xs ${LYRA_INPUT}`} />
              <Input value={h.value} onChange={(e) => setHeader(i, { value: e.target.value })} placeholder="Value" className={`h-8 flex-1 text-xs ${LYRA_INPUT}`}
                type={/key|secret|token/i.test(h.key || '') ? 'password' : 'text'} />
              <Button type="button" variant="ghost" size="icon" className="size-8 !rounded-none text-[#8a94a6] hover:bg-[#fdecec] hover:text-[#b42318]" onClick={() => onChange({ headers: headers.filter((_, j) => j !== i) })}><Trash2 className="size-3.5" /></Button>
            </div>
          ))}
          {!headers.length && <p className={`text-xs italic ${MUTED}`}>No custom headers. Content-Type: application/json is sent by default.</p>}
        </div>
      </div>
      <p className={`text-xs ${MUTED}`}>A failed send is retried after 1, 5 and 15 minutes, then marked failed (retry it from Runs).</p>
    </div>
  );
}

// The "+" between steps: pick what to insert.
function AddStep({ trigger, onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex flex-col items-center">
      <div className="h-3 w-px bg-[#c4cbd7]" />
      <button type="button" onClick={() => setOpen(!open)} aria-label="Add a step"
        className={cn('flex size-7 items-center justify-center border bg-white transition-colors', open ? 'border-[#3565e9] text-[#3565e9]' : 'border-[#c4cbd7] text-[#8a94a6] hover:border-[#3565e9] hover:text-[#3565e9]')}>
        <Plus className="size-4" />
      </button>
      <div className="h-3 w-px bg-[#c4cbd7]" />
      {open && (
        <div className="z-10 mb-2 grid w-full gap-2 border border-[#dde2e9] bg-white p-2 sm:grid-cols-2">
          {Object.entries(STEP_TYPES).filter(([, T]) => !T.purchaseOnly || trigger === 'checkout_purchase').map(([type, T]) => (
            <button key={type} type="button" onClick={() => { onAdd(type); setOpen(false); }}
              className="flex items-start gap-2.5 border border-[#e6e9ef] p-2.5 text-left hover:border-[#3565e9] hover:bg-[#f5f8ff]">
              <IconTile icon={T.icon} tone={T.tone} className="size-8 shrink-0" />
              <span><span className={`block text-sm font-medium ${INK}`}>{T.label}</span><span className={`block text-xs ${MUTED}`}>{T.desc}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// The flow: trigger at the top, then each step as a card, with "+" between them.
export function StepsEditor({ trigger, triggerLabel, triggerIcon, steps, onChange }) {
  const [openId, setOpenId] = useState(steps.length === 1 ? steps[0].id : null);
  const insert = (at, type) => {
    const s = blankStep(type);
    onChange([...steps.slice(0, at), s, ...steps.slice(at)]);
    setOpenId(s.id);
  };
  const patch = (id, p) => onChange(steps.map((s) => (s.id === id ? { ...s, ...p } : s)));
  const move = (i, d) => { const n = [...steps]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  const TriggerIcon = triggerIcon;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-3 border border-[#3565e9] bg-[#f5f8ff] px-3 py-2.5">
        <TriggerIcon className="size-4 text-[#3565e9]" />
        <span className={`${EYEBROW} text-[#2d5fdc]`}>Trigger</span>
        <span className={`text-sm font-medium ${INK}`}>{triggerLabel}</span>
      </div>
      <AddStep trigger={trigger} onAdd={(type) => insert(0, type)} />
      {steps.map((s, i) => {
        const T = STEP_TYPES[s.type];
        const open = openId === s.id;
        const problem = stepProblem(s, trigger);
        return (
          <React.Fragment key={s.id}>
            <div className={cn('border bg-white', open ? 'border-[#3565e9]' : problem ? 'border-[#f0b4ae]' : 'border-[#dde2e9]')}>
              <div className="flex cursor-pointer items-center gap-3 px-3 py-2.5" onClick={() => setOpenId(open ? null : s.id)}>
                <span className={`w-5 text-center text-xs font-semibold ${MUTED}`}>{i + 1}</span>
                <IconTile icon={T.icon} tone={T.tone} className="size-8 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${INK}`}>{s.name || T.label}</p>
                  <p className={cn('truncate text-xs', problem ? 'text-[#b42318]' : MUTED)}>{problem || stepSummary(s)}</p>
                </div>
                <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                  <Button type="button" variant="ghost" size="icon" className="size-7 !rounded-none text-[#8a94a6]" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up"><ArrowUp className="size-3.5" /></Button>
                  <Button type="button" variant="ghost" size="icon" className="size-7 !rounded-none text-[#8a94a6]" disabled={i === steps.length - 1} onClick={() => move(i, 1)} aria-label="Move down"><ArrowDown className="size-3.5" /></Button>
                  <Button type="button" variant="ghost" size="icon" className="size-7 !rounded-none text-[#8a94a6] hover:bg-[#fdecec] hover:text-[#b42318]" onClick={() => onChange(steps.filter((x) => x.id !== s.id))} aria-label="Delete step"><Trash2 className="size-3.5" /></Button>
                  <ChevronDown className={`ml-1 size-4 text-[#b5bfcd] transition-transform ${open ? 'rotate-180 text-[#3565e9]' : ''}`} />
                </div>
              </div>
              {open && (
                <div className="space-y-3 border-t border-[#eef1f5] px-4 pb-4 pt-3">
                  <div className="space-y-1.5">
                    <Label className={`text-xs ${INK}`}>Step name (optional)</Label>
                    <Input value={s.name} onChange={(e) => patch(s.id, { name: e.target.value })} placeholder={T.label} className={LYRA_INPUT} />
                  </div>
                  <StepFields step={s} onChange={(p) => patch(s.id, p)} />
                </div>
              )}
            </div>
            <AddStep trigger={trigger} onAdd={(type) => insert(i + 1, type)} />
          </React.Fragment>
        );
      })}
      {!steps.length && <p className={`text-center text-xs ${MUTED}`}>Add a step with the +.</p>}
    </div>
  );
}

// The card's one-line flow: Delay 5 min → Find GHL contact → n8n.ampong.co.uk
export function StepChips({ automation }) {
  const steps = stepsFromAutomation(automation);
  return (
    <div className="flex flex-wrap items-center gap-1">
      {steps.map((s, i) => {
        const T = STEP_TYPES[s.type] || STEP_TYPES.webhook;
        const Icon = T.icon;
        const text = s.name || (s.type === 'delay' ? `Wait ${mins(s.minutes)}` : s.type === 'webhook' ? host(s.url) : T.label);
        return (
          <React.Fragment key={s.id}>
            {i > 0 && <span className="text-xs text-[#b5bfcd]">→</span>}
            <span className="inline-flex max-w-full items-center gap-1.5 border border-[#e6e9ef] bg-[#f7f9fc] px-2 py-1 text-xs text-[#4b5566]" title={stepSummary(s)}>
              <Icon className="size-3 shrink-0 text-[#3565e9]" /><span className="truncate">{text}</span>
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const RUN_STATUS = { running: ['blue', 'Running'], waiting: ['gold', 'Waiting'], done: ['green', 'Done'], failed: ['red', 'Failed'] };
const OUTCOME_OK = ['done', 'found', 'sent', 'skipped', 'waiting'];
const when = (iso) => (iso ? `${fmtDate(iso)} · ${fmtTime(iso)}` : '');

// Runs: each sale's trip through the steps, step by step, with what it's waiting for.
export function RunsDialog({ automation, onClose }) {
  const [runs, setRuns] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/admin/automations/${automation.id}/runs`, auth()).then((r) => setRuns(r.data.runs))
      .catch(() => { setRuns([]); toast.error('Could not load runs'); });
  }, [automation.id]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const retry = async (run) => {
    setRetrying(run.id);
    try {
      const r = await axios.post(`${API}/admin/automation-runs/${run.id}/retry`, {}, auth());
      if (r.data.run.status === 'failed') toast.error('Still failing — see the last step'); else toast.success('Resumed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
    finally { setRetrying(null); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto !rounded-none border-[#dde2e9] bg-white">
        <DialogHeader>
          <p className={`${EYEBROW} text-[#2d5fdc]`}>Runs</p>
          <DialogTitle className={`text-xl tracking-[-0.5px] ${INK}`}>{automation.name}</DialogTitle>
          <DialogDescription className={`text-sm ${MUTED}`}>Each trigger's trip through the steps, newest first. Refreshes every 15 seconds.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className={LYRA_OUTLINE_BUTTON} onClick={load}><RotateCcw className="size-3.5" /> Refresh</Button>
        </div>
        {runs === null ? <p className={`py-10 text-center text-sm ${MUTED}`}><Loader2 className="mr-2 inline size-4 animate-spin" />Loading…</p>
          : !runs.length ? <p className={`py-10 text-center text-sm ${MUTED}`}>No runs yet. One starts each time the trigger fires.</p>
          : (
            <div className="space-y-3">
              {runs.map((run) => {
                const [tone, label] = RUN_STATUS[run.status] || ['gray', run.status];
                const who = run.who || {};
                const nextStep = run.steps?.[run.step_index];
                return (
                  <div key={run.id} className={`${LYRA_INSET} p-3`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`truncate text-sm font-medium ${INK}`}>{[who.first_name, who.last_name].filter(Boolean).join(' ') || who.email || 'Run'}
                          <span className={`font-normal ${MUTED}`}>{who.email ? ` · ${who.email}` : ''}{who.amount != null ? ` · $${who.amount}` : ''}{who.checkout_page ? ` · ${who.checkout_page}` : ''}</span></p>
                        <p className={`text-xs ${MUTED}`}>Started {when(run.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {run.manual && <Pill square tone="blue" dot={false}>Manual</Pill>}
                        <Pill square tone={tone}>{label}</Pill>
                        {run.status === 'failed' && (
                          <Button variant="outline" size="sm" className={LYRA_OUTLINE_BUTTON} onClick={() => retry(run)} disabled={retrying === run.id}>
                            {retrying === run.id ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" />} Retry
                          </Button>
                        )}
                      </div>
                    </div>
                    {run.status === 'waiting' && (
                      <p className="mt-2 text-xs text-[#8a5a00]"><Clock className="mr-1 inline size-3" />Next: {nextStep?.name || STEP_TYPES[nextStep?.type]?.label || 'step'} at {fmtTime(run.next_run_at)}</p>
                    )}
                    <ol className="mt-2 space-y-1 border-l border-[#dde2e9] pl-3">
                      {(run.history || []).map((h, i) => {
                        const ok = OUTCOME_OK.includes(h.outcome);
                        const bad = ['failed', 'stopped'].includes(h.outcome);
                        return (
                          <li key={i} className="flex items-start gap-2 text-xs">
                            {bad ? <XCircle className="mt-0.5 size-3 shrink-0 text-[#b42318]" /> : ok ? <CheckCircle className="mt-0.5 size-3 shrink-0 text-[#17794a]" /> : <Clock className="mt-0.5 size-3 shrink-0 text-[#b7791f]" />}
                            <span className={`w-14 shrink-0 ${MUTED}`}>{fmtTime(h.at)}</span>
                            <span className={INK}><span className="font-medium">{h.name || STEP_TYPES[h.type]?.label || h.type}</span> — {h.message}</span>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                );
              })}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

